import { Check, Loader2, Sparkles, Undo2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { diffWords, hasChanges } from "@shared/textDiff";

/**
 * The assistant attached to a professional biography field.
 *
 * Three states, and the middle one is the point: ask, watch it write, then decide.
 * A revision that simply replaced the field would leave the author unable to see
 * what had been taken out, so nothing is written until Approve is pressed and the
 * removed words stay on screen struck through until then.
 *
 * The typing is animated rather than the text appearing at once because the
 * revision arrives whole and lands as a wall of changed prose. Watching it write is
 * what makes the diff readable — and it is skippable, since anyone rereading a
 * revision they already saw should not have to wait for it twice.
 */
export default function BiographyAssistant({
  value,
  onApprove,
  maxLength = 8_000,
}: {
  value: string;
  onApprove: (revised: string) => void;
  maxLength?: number;
}) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [revised, setRevised] = useState<string | null>(null);
  const [typedLength, setTypedLength] = useState(0);
  const timerRef = useRef<number | null>(null);

  const revise = trpc.members.reviseBiography.useMutation({
    onSuccess: result => {
      setRevised(result.revised);
      setTypedLength(0);
      setNotesOpen(false);
    },
    onError: error => chiNotify.error("The biography was not revised", { description: error.message }),
  });

  /*
   * Typed out one character at a time on a timer, cleared on unmount and whenever a
   * new revision replaces the last — otherwise two revisions in a row animate
   * against each other.
   */
  useEffect(() => {
    if (revised === null) return;
    if (typedLength >= revised.length) return;
    timerRef.current = window.setTimeout(
      () => setTypedLength(length => Math.min(length + 3, revised.length)),
      12,
    );
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [revised, typedLength]);

  const isTyping = revised !== null && typedLength < revised.length;

  /*
   * Diffed against what has been typed so far, so the highlight grows with the
   * text. Diffing only the finished revision would show a plain block first and
   * repaint it the moment typing ended.
   */
  const segments = useMemo(
    () => (revised === null ? [] : diffWords(value, revised.slice(0, typedLength))),
    [value, revised, typedLength],
  );

  function reset() {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setRevised(null);
    setTypedLength(0);
  }

  const buttonBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: ".35rem",
    minHeight: 34,
    padding: ".35rem .7rem",
    borderRadius: ".5rem",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: "var(--chi-body-compact)",
    fontWeight: 700,
    cursor: "pointer",
  };

  return (
    <div style={{ display: "grid", gap: ".55rem", marginTop: ".45rem" }}>
      {revised === null && (
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
          <button
            type="button"
            disabled={revise.isPending}
            onClick={() => {
              if (!value.trim() && !notes.trim()) {
                setNotesOpen(true);
                return;
              }
              revise.mutate({ current: value, notes: notes.trim() || undefined });
            }}
            style={{
              ...buttonBase,
              border: "1px solid rgba(167,139,250,.55)",
              background: "linear-gradient(135deg,rgba(167,139,250,.24),rgba(125,234,255,.12))",
              color: "#E9D8FF",
            }}
          >
            {revise.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
            {revise.isPending ? "Writing…" : value.trim() ? "Improve with AI" : "Write with AI"}
          </button>
          <button
            type="button"
            onClick={() => setNotesOpen(open => !open)}
            style={{ ...buttonBase, border: "1px solid rgba(255,255,255,.14)", background: "transparent", color: "#94A3B8" }}
          >
            {notesOpen ? "Hide details" : "Add details"}
          </button>
          {/* Said plainly, because the field above asks for prose and this one does not. */}
          <span style={{ color: "#5F6B7D", fontFamily: "'Inter', sans-serif", fontSize: "calc(var(--chi-body-compact) * 0.88)", fontStyle: "italic" }}>
            Nothing is saved until you approve it.
          </span>
        </div>
      )}

      {revised === null && notesOpen && (
        <label style={{ display: "grid", gap: ".3rem" }}>
          <span style={{ color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
            Details to work from
          </span>
          <textarea
            value={notes}
            onChange={event => setNotes(event.target.value)}
            rows={3}
            maxLength={4_000}
            placeholder="Fragments are fine — 15 yrs talent acquisition, ex-Deloitte, based in Plano, speaks on hiring at scale"
            style={{
              width: "100%",
              padding: ".55rem .7rem",
              borderRadius: ".5rem",
              border: "1px solid rgba(255,255,255,.14)",
              background: "rgba(255,255,255,.05)",
              color: "#FFFFFF",
              fontFamily: "'Inter', sans-serif",
              fontSize: "var(--chi-body-compact)",
              lineHeight: 1.55,
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
        </label>
      )}

      {revised !== null && (
        <div style={{ display: "grid", gap: ".55rem", padding: ".8rem", border: "1px solid rgba(167,139,250,.3)", borderRadius: ".6rem", background: "rgba(167,139,250,.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".45rem", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", color: "#C4B5FD", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase" }}>
              <Sparkles size={13} aria-hidden="true" /> Suggested revision
            </span>
            {isTyping && (
              <button
                type="button"
                onClick={() => setTypedLength(revised.length)}
                style={{ ...buttonBase, minHeight: 26, padding: ".1rem .45rem", border: 0, background: "transparent", color: "#7DEAFF" }}
              >
                Skip animation
              </button>
            )}
          </div>

          <p aria-live="polite" style={{ margin: 0, color: "#E4EAF4", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
            {segments.map((segment, index) => {
              if (segment.kind === "unchanged") return <span key={index}>{segment.text}</span>;
              if (segment.kind === "added") {
                return (
                  <mark key={index} style={{ background: "rgba(134,239,172,.18)", color: "#BBF7D0", borderRadius: ".2rem", padding: "0 .1rem" }}>
                    {segment.text}
                  </mark>
                );
              }
              return (
                <span key={index} style={{ color: "#FCA5A5", textDecoration: "line-through", opacity: .75 }}>
                  {segment.text}
                </span>
              );
            })}
            {isTyping && <span aria-hidden="true" style={{ color: "#C4B5FD" }}>▍</span>}
          </p>

          {!isTyping && !hasChanges(segments) && (
            <p style={{ margin: 0, color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              The assistant did not change anything.
            </p>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={isTyping}
              onClick={() => {
                onApprove(revised.slice(0, maxLength));
                reset();
                setNotes("");
                chiNotify.success("Revision applied", { description: "Save the record to keep it." });
              }}
              style={{ ...buttonBase, border: 0, background: "linear-gradient(135deg,#E91E8C,#B91C72)", color: "#FFFFFF", opacity: isTyping ? .6 : 1, cursor: isTyping ? "wait" : "pointer" }}
            >
              <Check size={14} aria-hidden="true" /> Approve &amp; keep
            </button>
            <button
              type="button"
              onClick={reset}
              style={{ ...buttonBase, border: "1px solid rgba(248,113,113,.35)", background: "rgba(248,113,113,.08)", color: "#FCA5A5" }}
            >
              <X size={14} aria-hidden="true" /> Discard
            </button>
            <button
              type="button"
              disabled={revise.isPending}
              onClick={() => revise.mutate({ current: value, notes: notes.trim() || undefined })}
              style={{ ...buttonBase, border: "1px solid rgba(255,255,255,.14)", background: "transparent", color: "#94A3B8" }}
            >
              <Undo2 size={14} aria-hidden="true" /> Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
